import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Locks an element's height to fill the viewport, so its content scrolls INTERNALLY without the page
 * body scrolling. Returns a ref to attach + the measured pixel height (undefined until first measure).
 *
 * Key subtlety (adv-review): it's not enough to subtract the element's TOP from innerHeight — whatever
 * sits BELOW the element in normal flow (here: the page's bottom padding and #root's bottom
 * padding) would still overflow and produce a second, page-level
 * scrollbar. So we also measure the space below the element. That space moves RIGIDLY with the
 * element's bottom edge, so `body.bottom − el.bottom` is INVARIANT to the element's own height — a
 * non-circular measurement we can subtract too. Result: the element fills exactly the gap between the
 * chrome above and the chrome below, and nothing scrolls except the element's own content.
 *
 * Re-measures on window resize AND on any document-body size change (a ResizeObserver), so async
 * chrome above/below (roster-audit / impersonation banners) that appears WITHOUT a window
 * resize is accounted for. `safety` is a small floor to avoid sub-pixel overflow.
 */
export function useViewportFill(safety = 6) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      // everything below `el` (footer + trailing paddings) shifts with el.bottom, so this is invariant
      // to el's own height — measuring it is not circular.
      const below = document.body.getBoundingClientRect().bottom - rect.bottom;
      setHeight(Math.max(320, Math.round(window.innerHeight - rect.top - below - safety)));
    };
    measure();
    // re-measure on the next frame to catch layout that settles AFTER this commit — e.g. the app
    // chrome (big header/footer) being hidden for a full-viewport page, which shifts the top edge.
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    // catch chrome that changes height without a window resize (async banners). The math is
    // convergent (same layout → same height → React dedupes the setState), so this settles, it doesn't loop.
    // jsdom has no ResizeObserver (same guard as useStickyHeaderOffsets).
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(document.body);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [safety]);

  return { ref, height };
}
